import type { IconSizes } from "@smartsuite/icons";
import type { RefObject } from "react";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { WHEN_BUTTON_IS_CLICKED_TRIGGER_ID } from "./constants";

export const useOnClickOutside = <T extends HTMLElement>(
  ref: RefObject<T>,
  handler: (event: Event) => void,
): void => {
  const handlerRef = useRef(handler);
  useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const listener = (event: Event): void => {
      if (ref.current == null || ref.current.contains(event.target as Node)) {
        return;
      }
      handlerRef.current(event);
    };
    document.addEventListener("mousedown", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
    };
  }, [ref]);
};

export const useOnEscapeKeyPress = (fn: () => void): void => {
  const fnRef = useRef(fn);
  useLayoutEffect(() => {
    fnRef.current = fn;
  });

  useEffect(() => {
    const escFunction = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        fnRef.current();
      }
    };
    document.addEventListener("keydown", escFunction, false);
    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, []);
};

export const useGetSSTriggerLogo = (): ((label: string) => object) => {
  const getSSTriggerLogo = useMemo(
    () =>
      (label: string): object => {
        const triggerLabel = label.toLowerCase();
        const iconName: Record<string, string> = {
          "created-record": "plus-circle",
          "updated-record": "edit-smaller",
          schedule: "due-date",
          "matches-condition": "conditions",
          "form-submitted": "send",
          webhook: "webhook",
          [WHEN_BUTTON_IS_CLICKED_TRIGGER_ID]: "select-button",
        };
        const size: Record<string, IconSizes> = {
          "created-record": "M",
          "updated-record": "M",
          schedule: "S",
          "matches-condition": "S",
          "form-submitted": "M",
          webhook: "M",
          [WHEN_BUTTON_IS_CLICKED_TRIGGER_ID]: "M",
        };

        return {
          iconName: iconName[triggerLabel] || "plus-circle",
          iconSize: size[triggerLabel] || "M",
        };
      },
    [],
  );
  return getSSTriggerLogo;
};
